import React, { useState } from 'react';
import { useConfig } from '@context/ConfigContext';
import Modal from '@components/shared/Modal/Modal';
import styles from './Footer.module.css';

const Footer: React.FC = () => {
  const { config } = useConfig();
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);

  if (!config || !config.components.footer.enabled) return null;

  const { footer } = config.components;
  const { business, contact, social } = config;
  const currentYear = new Date().getFullYear();

  const socialPlatforms = [
    { name: 'Facebook', url: social.facebook, icon: 'fab fa-facebook' },
    { name: 'Instagram', url: social.instagram, icon: 'fab fa-instagram' },
    { name: 'LinkedIn', url: social.linkedin, icon: 'fab fa-linkedin' },
    { name: 'Twitter', url: social.twitter, icon: 'fab fa-twitter' },
    { name: 'YouTube', url: social.youtube, icon: 'fab fa-youtube' },
  ].filter((platform) => platform.url);

  const navItems = [
    { label: 'Home', href: '#home', enabled: config.components.hero.enabled },
    { label: 'About', href: '#about', enabled: config.components.about.enabled },
    { label: 'Services', href: '#services', enabled: config.components.services.enabled },
    { label: 'Portfolio', href: '#portfolio', enabled: config.components.portfolio.enabled },
    { label: 'Testimonials', href: '#testimonials', enabled: config.components.testimonials.enabled },
    { label: 'FAQ', href: '#faq', enabled: config.components.faq.enabled },
    { label: 'Contact', href: '#contact', enabled: config.components.contact.enabled },
  ].filter((item) => item.enabled);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();

    const element = document.querySelector(targetId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleLegalClick = (e: React.MouseEvent<HTMLAnchorElement>, type: 'privacy' | 'terms') => {
    const legalContent = type === 'privacy' ? footer.privacyPolicy : footer.terms;
    
    // Check if it's a modal (object) or link (string)
    if (typeof legalContent === 'object') {
      e.preventDefault();
      setActiveModal(type);
    }
    // If it's a string (URL), let the default link behavior happen
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  const getModalContent = () => {
    if (activeModal === 'privacy' && typeof footer.privacyPolicy === 'object') {
      return footer.privacyPolicy;
    }
    if (activeModal === 'terms' && typeof footer.terms === 'object') {
      return footer.terms;
    }
    return null;
  };

  const modalContent = getModalContent();

  return (
    <>
      <footer className={styles.footer}>
        <div className={styles.container}>
          <div className={styles.footerContent}>
            {/* About Column */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>About {business.name}</h3>
              <p className={styles.about}>{footer.about}</p>
              {business.showABN && business.abn && (
                <p className={styles.abn}>ABN: {business.abn}</p>
              )}
            </div>

            {/* Quick Links */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Quick Links</h3>
              <ul className={styles.linkList}>
                {navItems.map((item) => (
                  <li key={item.href}>
                    <a
                      href={item.href}
                      onClick={(e) => handleNavClick(e, item.href)}
                      className={styles.link}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Contact Us</h3>
              <ul className={styles.contactList}>
                <li className={styles.contactItem}>
                  <i className="fas fa-phone" />
                  <a 
                    href={`tel:${contact.phoneRaw}`}
                    onClick={(e) => (e.currentTarget as HTMLAnchorElement).blur()}
                  >
                    {contact.phone}
                  </a>
                </li>
                {contact.email && (
                  <li className={styles.contactItem}>
                    <i className="fas fa-envelope" />
                    <a 
                      href={`mailto:${contact.email}`}
                      onClick={(e) => (e.currentTarget as HTMLAnchorElement).blur()}
                    >
                      {contact.email}
                    </a>
                  </li>
                )}
                {contact.website && (
                  <li className={styles.contactItem}>
                    <i className="fas fa-globe" />
                    <a 
                      href={`https://${contact.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => (e.currentTarget as HTMLAnchorElement).blur()}
                    >
                      {contact.website}
                    </a>
                  </li>
                )}
              </ul>
            </div>

            {/* Social Media */}
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>Follow Us</h3>
              {socialPlatforms.length > 0 ? (
                <div className={styles.socialIcons}>
                  {socialPlatforms.map((platform) => (
                    <a
                      key={platform.name}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={platform.name}
                      className={styles.socialIcon}
                    >
                      <i className={platform.icon} />
                    </a>
                  ))}
                </div>
              ) : (
                <p className={styles.noSocial}>Connect with us</p>
              )}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className={styles.bottomBar}>
            <div className={styles.copyright}>
              <p>&copy; {currentYear} {business.name}. All rights reserved.</p>
            </div>
            <div className={styles.legalLinks}>
              <a 
                href={typeof footer.privacyPolicy === 'string' ? footer.privacyPolicy : '#privacy'} 
                className={styles.legalLink}
                onClick={(e) => handleLegalClick(e, 'privacy')}
              >
                Privacy Policy
              </a>
              <span className={styles.separator}>|</span>
              <a 
                href={typeof footer.terms === 'string' ? footer.terms : '#terms'} 
                className={styles.legalLink}
                onClick={(e) => handleLegalClick(e, 'terms')}
              >
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Content Modal */}
      {modalContent && (
        <Modal
          isOpen={true}
          onClose={closeModal}
          title={modalContent.title}
          size="lg"
        >
          <div 
            className={styles.legalContent}
            dangerouslySetInnerHTML={{ __html: modalContent.content }}
          />
        </Modal>
      )}
    </>
  );
};

export default Footer;